import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { getUserToken } from "utils/helperFunctions";
import type {
  CreateMeetingParams,
  CreateMeetingResponse,
  CreateRecurringMeetingParams,
  CreateRecurringMeetingResponse,
  GetAllDetectedMeetingsResponse,
  GetDetectedMeetingsResponse,
  UpdateMeetingStatusParams,
  UpdateMeetingStatusResponse,
  UserCalendarSettingsResponse,
} from "./types";

/* --------------------------------------------------------
  API SLICE
-------------------------------------------------------- */

export const calendarApi = createApi({
  reducerPath: "calendarApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${REACT_APP_API_URL}/talentInsights`,
    prepareHeaders: (headers) => {
      const token = getUserToken();
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["DetectedMeeting", "AllDetectedMeeting"],
  endpoints: (builder) => ({
    getDetectedMeetings: builder.query<GetDetectedMeetingsResponse, void>({
      query: () => "calendar-meetings/detected",
      providesTags: (result) => {
        if (!result) return [{ type: "DetectedMeeting" as const, id: "LIST" }];
        return [
          { type: "DetectedMeeting" as const, id: "LIST" },
          ...(result.detected_1on1s ?? []).map((meeting) => ({
            type: "DetectedMeeting" as const,
            id: meeting.id,
          })),
        ];
      },
    }),

    getAllDetectedMeetings: builder.query<GetAllDetectedMeetingsResponse, void>(
      {
        query: () => "calendar-meetings/detected-all",
        providesTags: (result) => {
          if (!result)
            return [{ type: "AllDetectedMeeting" as const, id: "LIST" }];
          return [
            { type: "AllDetectedMeeting" as const, id: "LIST" },
            ...(result.detected_1on1s ?? []).map((meeting) => ({
              type: "AllDetectedMeeting" as const,
              id: meeting.id,
            })),
          ];
        },
      }
    ),

    updateMeetingStatus: builder.mutation<
      UpdateMeetingStatusResponse,
      UpdateMeetingStatusParams
    >({
      query: ({ meetingId, action }) => ({
        url: `calendar-meetings/${meetingId}/status`,
        method: "PUT",
        body: { action },
      }),
      async onQueryStarted(
        { meetingId, action },
        { dispatch, queryFulfilled }
      ) {
        const now = new Date().toISOString();

        const detectedPatch = dispatch(
          calendarApi.util.updateQueryData(
            "getDetectedMeetings",
            undefined,
            (draft) => {
              const idx = draft.detected_1on1s.findIndex(
                (meeting) => meeting.id === meetingId
              );
              if (idx !== -1) draft.detected_1on1s.splice(idx, 1);
            }
          )
        );

        const allDetectedPatch = dispatch(
          calendarApi.util.updateQueryData(
            "getAllDetectedMeetings",
            undefined,
            (draft) => {
              const meeting = draft.detected_1on1s.find(
                (m) => m.id === meetingId
              );
              if (!meeting) return;
              meeting.userConfirmed = action === "confirm";
              meeting.userConfirmedAt = action === "confirm" ? now : null;
              meeting.userRejected = action === "reject";
              meeting.userRejectedAt = action === "reject" ? now : null;
              meeting.rejected = action === "reject";
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          detectedPatch.undo();
          allDetectedPatch.undo();
        }
      },
      invalidatesTags: (_, __, { meetingId }) => [
        { type: "DetectedMeeting", id: meetingId },
        { type: "DetectedMeeting", id: "LIST" },
        { type: "AllDetectedMeeting", id: meetingId },
        { type: "AllDetectedMeeting", id: "LIST" },
      ],
    }),

    createMeeting: builder.mutation<CreateMeetingResponse, CreateMeetingParams>(
      {
        query: (body) => ({
          url: "calendar-meetings/create",
          method: "POST",
          body,
        }),
      }
    ),

    createRecurringMeeting: builder.mutation<
      CreateRecurringMeetingResponse,
      CreateRecurringMeetingParams
    >({
      query: (body) => ({
        url: "calendar-meetings/create-recurring",
        method: "POST",
        body,
      }),
    }),

    getUserCalendarSettings: builder.query<UserCalendarSettingsResponse, void>({
      query: () => "user/calendar-settings",
    }),
  }),
});

/* --------------------------------------------------------
  EXPORT HOOKS
-------------------------------------------------------- */

export const {
  useGetDetectedMeetingsQuery,
  useGetAllDetectedMeetingsQuery,
  useUpdateMeetingStatusMutation,
  useCreateMeetingMutation,
  useCreateRecurringMeetingMutation,
  useGetUserCalendarSettingsQuery,
} = calendarApi;
